import ABCyaLib from '@abcya/abcya_lib';
import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import MenuButton from '../game/MenuButton';
import OptionLayer from '../game/OptionLayer';
import PinchZoomContainer from '../game/PinchZoomContainer';
import gameData from '../gameData';
import SoundAssetKeys from '../SoundAssetKeys';
import UIAssetKeys from '../UIAssetKeys';
import { parseWords } from '../Utils';
import { WordCloud } from '../wordcloud-phaser';
import InputScreen from './InputScreen';
import FontKeys from '../FontKeys';

export default class Game extends Phaser.Scene
{
    static get KEY() { return 'Game'; }

    create()
    {
        this.fontColor = 'random-dark';
        this.backColor = '#fff';
        this.layout = "random";
        this.wordLimit = 200;
        this.fontFamily = "jetset";
        this.offscreenCanvas = null;
        this.wordBitmaps = [];
        this.maskImage = null;
        this.shape = 'circle';
        this.weightFactor = 4;
        this.loadingMessage = null;

        this.shapesArray = ["circle", "pentagon", "triangle", "diamond", "star", "cardioid"];

        this.colorArray = [
            [0, 0, 0], [51, 51, 51], [102, 102, 102], [153, 153, 153], [204, 204, 204],
            [10, 95, 96], [100, 31, 50], [84, 158, 56], [171, 184, 47], [72, 184, 228],
            [4, 33, 87], [7, 52, 138], [33, 96, 163], [58, 167, 222], [72, 184, 228],
            [249, 81, 111], [234, 57, 46], [242, 102, 34], [245, 139, 40], [254, 222, 101],
            [253, 147, 136], [254, 199, 133], [254, 220, 131], [255, 249, 197], [124, 241, 210],
            [201, 57, 208], [224, 72, 144], [250, 95, 175], [253, 137, 170], [254, 197, 207],
            [26, 174, 150], [26, 177, 104], [49, 209, 83], [108, 219, 59], [154, 235, 63],
            [202, 120, 59], [219, 148, 57], [210, 168, 62], [191, 178, 113], [255, 255, 240],
            [65, 119, 92], [161, 48, 23], [176, 77, 36], [253, 175, 72], [254, 238, 169],
            [165, 133, 252], [107, 217, 237], [167, 224, 61], [246, 147, 51], [246, 45, 115],
            [6, 63, 60], [138, 99, 53], [230, 145, 48], [251, 194, 81], [223, 203, 124],
            [255, 255, 255], [204, 204, 204], [153, 153, 153], [102, 102, 102], [51, 51, 51]

        ];

        this.backColorArray = [
            [255, 255, 255],
            [255, 255, 239],
            [221, 255, 251],
            [253, 186, 49],
            [26, 170, 169],
            [101, 25, 117],
            [10, 51, 62],
            [57, 28, 7],
            [38, 38, 38],
            [33, 38, 38],
            [3, 31, 32],
            [1, 1, 1]

        ];

        this.fontArray = gameData.fonts;

        // turn off the 'edit' option once we have retuned to this cloud generating screen
        // this variable is only set from 'edit' option item in menu
        gameData.EDIT_SELECTED = false;

        this.backgroundShape2 = this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0xffffff).setOrigin(0);

        //pinch-zoom container
        this.pzContainer = new PinchZoomContainer(this);
        this.add.existing(this.pzContainer);

        this.createMenuButtons();
        this.randomizeButtonClicked();
    }

    createMenuButtons()
    {
        this.menuButton = new MenuButton(this, "menu_button");
        this.add.existing(this.menuButton);
        this.menuButton.x = 0;
        this.menuButton.y = 0;
        this.menuButton.on("SELECTED", this.menuButtonClicked, this);
        this.menuButton.on("UNSELECTED", this.buttonClickedOff, this);

        this.wordButton = new MenuButton(this, "word_limit_button");
        this.add.existing(this.wordButton);
        this.wordButton.x = this.menuButton.width - 1;
        this.wordButton.y = 0;
        this.wordButton.on("SELECTED", this.wordButtonClicked, this);
        this.wordButton.on("UNSELECTED", this.buttonClickedOff, this);

        this.randomizeButton = this.add.container(0,0);
        const randomizeBtnBg = ABCyaPhaser.Images.addImage(0,0,UIAssetKeys._randomize_button, this.randomizeButton).setOrigin(0);
        ABCyaPhaser.Container.setContainerInteractive(this.randomizeButton);
        const randomizeBtnTxt = ABCyaPhaser.Text.addText(111,randomizeBtnBg.height/2,16, 0xffffff, FontKeys._BPreplayBold, ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.randomize_button"), this.randomizeButton).setResolution(2);
        ABCyaPhaser.Container.makeContainerTintable(this.randomizeButton);
        ABCyaPhaser.Buttons.convertToButton(this.randomizeButton, {});
        this.randomizeButton.x = 2 * (this.menuButton.width - 1);
        this.randomizeButton.y = 0;
        this.randomizeButton.on(ABCyaPhaser.Buttons.Events.ButtonEvents.CLICK, this.randomizeButtonClicked, this);

        this.layoutButton = new MenuButton(this, "layout_button");
        this.add.existing(this.layoutButton);
        this.layoutButton.x = 3 * (this.menuButton.width - 1);
        this.layoutButton.y = 0;
        this.layoutButton.on("SELECTED", this.layoutButtonClicked, this);
        this.layoutButton.on("UNSELECTED", this.buttonClickedOff, this);

        this.colorButton = new MenuButton(this, "color_button");
        this.add.existing(this.colorButton);
        this.colorButton.x = 4 * (this.menuButton.width - 1);
        this.colorButton.y = 0;
        this.colorButton.on("SELECTED", this.colorButtonClicked, this);
        this.colorButton.on("UNSELECTED", this.buttonClickedOff, this);

        this.fontButton = new MenuButton(this, "font_button");
        this.add.existing(this.fontButton);
        this.fontButton.x = 5 * (this.menuButton.width - 1);
        this.fontButton.y = 0;
        this.fontButton.on("SELECTED", this.fontButtonClicked, this);
        this.fontButton.on("UNSELECTED", this.buttonClickedOff, this);
    }

    menuButtonClicked()
    {
        this.menuOptionLayer = new OptionLayer(this, "m", ABCyaPhaser.Images.DEFAULT_ATLAS, 
        [
          ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.menu_options.new_button"),
          ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.menu_options.edit_button"),
          ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.menu_options.save_button"),
          ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.menu_options.print_button"),
          ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.menu_options.word_filter_button"),
          ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.menu_options.help_button"),
        ], 6, 4);
        this.add.existing(this.menuOptionLayer);
        this.menuOptionLayer.on("selected", this.handleMenuOptions, this);

        //handle dismiss menu events
        this.menuOptionLayer.on("DISMISS", this.handleDismissMenu, this);
    }

    handleMenuOptions(index)
    {
        switch (index)
        {
            case 1: //new
                this.scene.start(InputScreen.KEY);
                break;

            case 2: //edit
                gameData.EDIT_SELECTED = true;
                this.scene.start(InputScreen.KEY);
                break;

            case 3: //save
                this.saveAction();
                break;

            case 4: //print
                this.printAction();
                break;

            case 5: //word filter
                this.toggleWordFilter();
                break;

            case 6: //help
                this.showHelpPage();
                break;
        }

        if (index != 5 && index != 4 && index != 3)
        {
            this.menuButton.highlightOff();
            this.menuOptionLayer.destroy();
        }
    }

    handleDismissMenu()
    {
        MenuButton.highlightOff();
        this.menuOptionLayer.destroy();
    }

    buttonClickedOff()
    {
        this.menuOptionLayer.destroy();
    }

    saveAction()
    {
        let rt = new Phaser.GameObjects.RenderTexture(this, 0, 0, this.scale.width, this.scale.height - 50);
        rt.draw([this.backgroundShape2, this.pzContainer], 0, -50);
        rt.snapshot(async s =>
        {
            try
            {
                await ABCyaLib.PrintSave.instance.saveImage(s, { fileName: ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.filename") });
            }
            catch (ex)
            {
                console.warn(ex);
            }
            rt.destroy();
        });

        this.menuButton.highlightOff();
        this.menuOptionLayer.destroy();
    }

    printAction()
    {
        let rt = new Phaser.GameObjects.RenderTexture(this, 0, 0, this.scale.width, this.scale.height - 50);
        rt.draw([this.backgroundShape2, this.pzContainer], 0, -50);
        rt.snapshot(async s =>
        {
            try
            {
                await ABCyaLib.PrintSave.instance.printImage(s);
            }
            catch (ex)
            {
                console.warn(ex);
            }
            rt.destroy();
        });

        this.menuButton.highlightOff();
        this.menuOptionLayer.destroy();
    }

    toggleWordFilter()
    {
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._click, this);

        this.menuOptionLayer.toggleWordFilter();

        //re-parse the words turning word filter ON or OFF
        this.parseWords();

        this.removeWordFilterOptionLayer();

        this.makeCloud();
    }

    parseWords()
    {
        let string = gameData.CURRENT_TEXT;
        if (string == '')
        {
            ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._error, this);
            return;
        }

        parseWords(string);
    }

    removeWordFilterOptionLayer()
    {
        this.menuButton.highlightOff();
        this.menuOptionLayer.destroy();
    }

    makeCloud()
    {
        //show loading message
        this.loadingMessage = this.add.container(this.scale.width/2, this.scale.height/2,
        [
          this.add.rectangle(0,0,this.scale.width, 62, 0xdcfdff),
          ABCyaPhaser.Text.addText(0,0,24, 0x2cbcdd, FontKeys._BPreplayBold, ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.loading_message"), this, true).setResolution(2),
        ]);

        if (WordCloud.isSupported)
        {
            let settings = {
                list: gameData.WORD_LIST,
                fontFamily: this.fontFamily,
                fontWeight: 'normal',
                color: this.fontColor,
                minSize: 4, // 0 to disable, 8 before
                weightFactor: this.weightFactor, //was 32
                gridSize: 8,
                clearCanvas: true,
                backgroundColor: this.backColor,  // opaque white = rgba(255, 255, 255, 1)
                drawOutOfBound: false,
                origin: null,
                drawMask: false,
                maskColor: 'rgba(255,0,0,0.3)',
                maskGapWidth: 0.3,
                wait: 0,
                abortThreshold: 0, // disabled
                abort: function noop()
                {
                },
                minRotation: -Math.PI / 2,
                maxRotation: Math.PI / 2,
                shuffle: true,
                rotateRatio: 0.1,
                shape: this.shape,
                ellipticity: 1,
                // layout can be horizontal, vertical, mixed, random
                layout: this.layout,
                classes: null,
                hover: null,
                click: null
            };

            this.offscreenCanvas = document.createElement('canvas');

            this.offscreenCanvas.setAttribute('width', this.scale.width);
            this.offscreenCanvas.setAttribute('height', this.scale.height - 50);
            this.offscreenCanvas.id = 'offscreen';
            this.offscreenCanvas.addEventListener('wordcloudcomplete', this.doCloudTransition.bind(this));

            this.backgroundShape2?.destroy();

            // while making WordCloud, lock all buttons
            this.disableButtons();
            WordCloud.create(this, this.offscreenCanvas, settings);
            this.createNewBackground();
        }
        else
        {
            console.warn("Browser does not support word cloud features.");
        }
    }

    enableButtons()
    {
        this.menuButton.setEnabled(true);
        this.wordButton.setEnabled(true);
        this.randomizeButton.setEnabled(true);
        this.layoutButton.setEnabled(true);
        this.colorButton.setEnabled(true);
        this.fontButton.setEnabled(true);
    }

    disableButtons()
    {
        this.menuButton.setEnabled(false);
        this.wordButton.setEnabled(false);
        this.randomizeButton.setEnabled(false);
        this.layoutButton.setEnabled(false);
        this.colorButton.setEnabled(false);
        this.fontButton.setEnabled(false);
    }

    createNewBackground()
    {
        this.backgroundShape2 = this.add.rectangle(0, 0, this.scale.width, this.scale.height, Phaser.Display.Color.HexStringToColor(this.backColor).color).setOrigin(0);
        this.children.sendToBack(this.backgroundShape2);
        this.backgroundShape2.alpha = 0;
        ABCyaPhaser.Tweens.addTween(this.backgroundShape2, 1000, { alpha: 1, delay: 200 }, Phaser.Math.Easing.Linear);
    }

    showHelpPage()
    {
        this.disableButtons();
        this.helpPage = this.add.container(this.scale.width/2, this.scale.height/2, 
        [
          ABCyaPhaser.Images.addImage(0,0, UIAssetKeys._help_popup, this),
          ABCyaPhaser.Text.addText(-323, -174,22, 0x2cbcdd, FontKeys._BPreplayBold, ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.help_popup.help_header"), this, false).setOrigin(0, 0.5).setResolution(2),
          ABCyaPhaser.Text.addText(-323, -15,22, 0x2cbcdd, FontKeys._BPreplayBold, ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.help_popup.tips_header"), this, false).setOrigin(0, 0.5).setResolution(2),
          ABCyaPhaser.Text.addText(-325,-140,16, 0x005e87, FontKeys._BPreplay, ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.help_popup.help_text"), this, false).setOrigin(0).setWordWrapWidth(650)
        ]);

        const tip1 = ABCyaPhaser.Text.addText(-325,20,16, 0x005e87, FontKeys._BPreplay, "- " + ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.help_popup.tip_1"), this.helpPage, false).setOrigin(0).setWordWrapWidth(650);
        const tip2 = ABCyaPhaser.Text.addText(-325,tip1.y + tip1.height + 24,16, 0x005e87, FontKeys._BPreplay, "- " + ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.help_popup.tip_2"), this.helpPage, false).setOrigin(0).setWordWrapWidth(650);
        const tip3 = ABCyaPhaser.Text.addText(-325,tip2.y + tip2.height + 24,16, 0x005e87, FontKeys._BPreplay, "- " + ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.help_popup.tip_3"), this.helpPage, false).setOrigin(0).setWordWrapWidth(650);

        ABCyaPhaser.Container.setContainerInteractive(this.helpPage, false);
        this.helpPage.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.removeHelpPage, this);
    }

    removeHelpPage()
    {
        this.enableButtons();
        this.helpPage.destroy();
    }

    randomizeButtonClicked()
    {
        MenuButton.highlightOff();

        this.fontColor = 'random-dark';
        this.backColor = '#fff';
        this.layout = "random";
        this.wordLimit = 50;
        this.fontFamily = "jetset";

        //randomize color
        let randomColorIndex = Phaser.Math.Between(1, 70);

        if (randomColorIndex >= 65)
        {
            this.fontColor = 'random-dark';
            this.backColor = makeHexColor(255, 255, 255);
        }
        else if (randomColorIndex > 60)
        {
            this.fontColor = 'random-light';
            this.backColor = makeHexColor(0, 0, 0);
        }
        else
        {
            this.chooseRandomColors();
        }

        //randomize layout
        let randomLayoutIndex = Phaser.Math.Between(0, 2);
        //take out vertical from random array
        let layoutArray = ["mixed", "horizontal", "random"];
        this.layout = layoutArray[randomLayoutIndex];

        //randomize word limit
        let randomLimitIndex = Phaser.Math.Between(0, 7);

        let wordLimitArray = [10, 50, 100, 200, 300, 400, 500, 1000];
        this.wordLimit = wordLimitArray[randomLimitIndex];

        this.processWordLimitFrequencies();

        //randomize shape
        let randomShapeIndex = Phaser.Math.Between(0, 5);
        this.shape = this.shapesArray[randomShapeIndex];

        //randomize fonts
        let randomFontIndex = Phaser.Math.Between(0, 11);
        this.fontFamily = this.fontArray[randomFontIndex];

        this.makeCloud();
    }

    chooseRandomColors()
    {
        let index = Phaser.Math.Between(1, 12);

        let hslColorArray = [];
        for (let i = ((index - 1) * 5); i < ((index - 1) * 5) + 5; i++)
        {
            let co = this.colorArray[i];
            let hslString = this.decimalToHSL(co[0], co[1], co[2]);
            hslColorArray.push(hslString);
        }

        let bA = this.backColorArray[index - 1];

        let colorFunction = function getRandomColorSet()
        {
            let randNo = Phaser.Math.Between(0, 4);
            let colorString = hslColorArray[randNo];

            return colorString;
        };

        this.fontColor = colorFunction;
        this.backColor = makeHexColor(bA[0], bA[1], bA[2]);
    }

    processWordLimitFrequencies()
    {
        let sortedKeys = getSortedKeys(gameData.FREQUENCIES);

        let wordArray = [];
        let count = 0;

        let lowestFrequency = 999;

        for (let i = sortedKeys.length - 1; i > -1; i--)
        {
            let word = sortedKeys[i];
            let freq = gameData.FREQUENCIES[word];

            count++;
            wordArray.push([word, freq]);

            //summarize the frequencies now in order to calculate a best weightFactor
            // in order to keep clouds on canvas.
            if (freq < lowestFrequency)
            {
                lowestFrequency = freq;
            }

            if (count >= this.wordLimit) break;
        }

        //because when we sort by word frequency, all the highest frequency words are being used
        // and therefore scaling too large (causing many word drops).  lets divide the frequencies by the lowest frequency to scale
        let scaledWordArray = [];
        let totalFrequency = 0;

        for (let i = 0; i < wordArray.length; i++)
        {
            let wordAndFreq = wordArray[i];
            let word = wordAndFreq[0];
            let freq = wordAndFreq[1];

            let newFrequency = freq / lowestFrequency;

            newFrequency = Math.floor(newFrequency);

            if (newFrequency < 1)
            {
                newFrequency = 1;
            }

            totalFrequency += newFrequency;

            scaledWordArray.push([word, newFrequency]);
        }

        let factor = 1;
        let size = scaledWordArray.length;

        if (size > 1 && size <= 10)
        {
            factor = 1;
        }
        else if (size > 10 && size <= 20)
        {
            factor = 1.5;
        }
        else if (size > 20 && size <= 50)
        {
            factor = 1.7;
        }
        else if (size > 50 && size <= 100)
        {
            factor = 2;
        }
        else if (size > 10 && size <= 200)
        {
            factor = 2.2;
        }
        else if (size > 200 && size <= 300)
        {
            factor = 2.5;
        }
        else if (size > 300 && size <= 400)
        {
            factor = 3;
        }
        else if (size > 400 && size <= 500)
        {
            factor = 3.5;
        }
        else if (size > 500 && size <= 1000)
        {
            factor = 4;
        }

        this.weightFactor = (1000 / totalFrequency) * factor;

        if (this.weightFactor > 200)
        {
            this.weightFactor = 200;
        }

        gameData.WORD_LIST = scaledWordArray;
    }

    decimalToHSL(d1, d2, d3)
    {
        let rgb = {};
        rgb.r = d1 / 255;
        rgb.g = d2 / 255;
        rgb.b = d3 / 255;
        rgb.a = 1;

        let hsl = rgbToHsl(d1, d2, d3);

        let h = hsl[0] * 360;
        let s = hsl[1] * 100;
        let l = hsl[2] * 100;
        let string = "hsl(" + h + ',' + s + '%,' + l + '%)';

        return string;
    }

    doCloudTransition(evt)
    {
        let transChoice = Phaser.Math.Between(1, 6);

        switch (transChoice)
        {
            case 1:
                this.makeWordBitmap1(evt);
                break;
            case 2:
                this.makeWordBitmap2(evt);
                break;
            case 3:
                this.makeWordBitmap3(evt);
                break;
            case 4:
                this.makeWordBitmap4(evt);
                break;
            case 5:
                this.makeWordBitmap5(evt);
                break;
            case 6:
                this.makeWordBitmap6(evt);
                break;
        }
    }

    makeWordBitmap1(evt)
    {
        for (let i = 0; i < this.wordBitmaps.length; i++)
        {
            let randX = Phaser.Math.Between(0, 1);
            let randY = Phaser.Math.Between(0, 1);

            let image = this.wordBitmaps[i];

            if (randX == 0)
            {
                ABCyaPhaser.Tweens.addTween(image, 1000, { x: image.x + 1000 }, Phaser.Math.Easing.Linear, () => this.pzContainer.removeIt(image));
            }
            else
            {
                ABCyaPhaser.Tweens.addTween(image, 1000, { x: image.x - 1000 }, Phaser.Math.Easing.Linear, () => this.pzContainer.removeIt(image));
            }

            if (randY == 0)
            {
                ABCyaPhaser.Tweens.addTween(image, 1000, { x: image.y + 700 }, Phaser.Math.Easing.Linear);
            }
            else
            {
                ABCyaPhaser.Tweens.addTween(image, 1000, { x: image.y - 700 }, Phaser.Math.Easing.Linear);
            }

            let randSpins = Phaser.Math.Between(0, 1);
            ABCyaPhaser.Tweens.addTween(image, 1500, { angle: randSpins * 360 }, Phaser.Math.Easing.Linear);
        }

        let wordImages = evt._images;
        let wordOffsets = evt._offsets;
        //make sure this is empty from last iteration!
        this.wordBitmaps = [];

        for (let i = 0; i < wordImages.length; i++)
        {
            let image = wordImages[i];
            this.pzContainer.addIt(image);
            this.wordBitmaps.push(image);

            let randX = Phaser.Math.Between(0, 1500) - 750;
            let randY = Phaser.Math.Between(0, 1000) - 500;
            let randSpins = Phaser.Math.Between(0, 1);

            image.x = wordOffsets[i][0] + randX;
            image.y = wordOffsets[i][1] + 50 + randY;
            image.alpha = 0;

            image.doubleClickAction = this.deleteWord.bind(this);

            this.tweens.timeline(
                {
                    tweens:
                        [
                            ABCyaPhaser.Tweens.createTween(image, 1500, { angle: randSpins * 360 }, Phaser.Math.Easing.Linear),
                            ABCyaPhaser.Tweens.createTween(image, 100, { alpha: 1, offset: 500 }, Phaser.Math.Easing.Linear),
                            ABCyaPhaser.Tweens.createTween(image, 1000, { x: image.x - randX, y: image.y - randY, offset: 600 }, Phaser.Math.Easing.Linear),
                            ABCyaPhaser.Tweens.createTween(image, 300, { scale: 0.9, offset: 1500 }, Phaser.Math.Easing.Linear),
                            ABCyaPhaser.Tweens.createTween(image, 700, { scale: 1, offset: 1800 }, Phaser.Math.Easing.Bounce.Out),
                        ]
                }
            );
        }

        this.loadingMessage.destroy();
        this.loadingMessage = null;

        this.time.delayedCall(2000, this.enableButtons, null, this);
    }

    makeWordBitmap2(evt)
    {
        for (let i = 0; i < this.wordBitmaps.length; i++)
        {
            let randX = Phaser.Math.Between(0, 1);
            let image = this.wordBitmaps[i];

            if (randX == 0)
            {
                ABCyaPhaser.Tweens.addTween(image, 1000, { scale: 5 }, Phaser.Math.Easing.Linear, () => this.pzContainer.removeIt(image));
            }
            else
            {
                ABCyaPhaser.Tweens.addTween(image, 1000, { scale: 0.1 }, Phaser.Math.Easing.Linear, () => this.pzContainer.removeIt(image));
            }

            let randSpins = Phaser.Math.Between(0, 1);

            ABCyaPhaser.Tweens.addTween(image, 1500, { angle: randSpins * 360 }, Phaser.Math.Easing.Linear);
        }

        let wordImages = evt._images;
        let wordOffsets = evt._offsets;
        //make sure this is empty from last iteration!
        this.wordBitmaps = [];

        for (let i = 0; i < wordImages.length; i++)
        {
            let image = wordImages[i];
            this.pzContainer.addIt(image);
            this.wordBitmaps.push(image);

            let spinChoice = Phaser.Math.Between(0, 1);

            image.x = wordOffsets[i][0] + 0;
            image.y = wordOffsets[i][1] + 50 + 0;
            image.alpha = 0;
            image.scale = 2;

            image.doubleClickAction = this.deleteWord.bind(this);

            ABCyaPhaser.Tweens.addTween(image, 100, { alpha: 1, delay: 500 }, Phaser.Math.Easing.Linear);
            ABCyaPhaser.Tweens.addTween(image, 1000, { scale: 1, delay: 600 }, Phaser.Math.Easing.Linear);

            if (spinChoice == 0)
            {
                ABCyaPhaser.Tweens.addTween(image, 1500, { angle: 360 }, Phaser.Math.Easing.Linear);
            }
            else
            {
                ABCyaPhaser.Tweens.addTween(image, 1500, { angle: -360 }, Phaser.Math.Easing.Linear);
            }
        }

        this.loadingMessage.destroy();
        this.loadingMessage = null;

        this.time.delayedCall(2000, this.enableButtons, null, this);
    }


    makeWordBitmap3(evt)
    {
        let wait = 0;

        for (let i = 0; i < this.wordBitmaps.length; i++)
        {
            let randX = Phaser.Math.Between(0, 1);
            let image = this.wordBitmaps[i];

            wait += 20;

            if (wait > 500)
            {
                wait = 500;
            }

            if (randX == 0)
            {
                ABCyaPhaser.Tweens.addTween(image, 1000, { x: image.x + 1000, delay: wait }, Phaser.Math.Easing.Linear, () => this.pzContainer.removeIt(image));
            }
            else
            {
                ABCyaPhaser.Tweens.addTween(image, 1000, { x: image.x - 1000, delay: wait }, Phaser.Math.Easing.Linear, () => this.pzContainer.removeIt(image));
            }
        }

        let wordImages = evt._images;
        let wordOffsets = evt._offsets;
        //make sure this is empty from last iteration!
        this.wordBitmaps = [];

        wait = 0;

        for (let i = 0; i < wordImages.length; i++)
        {
            let image = wordImages[i];
            this.pzContainer.addIt(image);
            this.wordBitmaps.push(image);

            let randX = Phaser.Math.Between(0, 1500) - 750;

            image.x = wordOffsets[i][0] + randX;
            image.y = wordOffsets[i][1] + 50 + 0;
            image.alpha = 0;

            image.doubleClickAction = this.deleteWord.bind(this);

            wait += 20;

            if (wait > 1000)
            {
                wait = 1000;
            }

            ABCyaPhaser.Tweens.addTween(image, 100, { alpha: 1, delay: 500 + wait }, Phaser.Math.Easing.Linear);
            ABCyaPhaser.Tweens.addTween(image, 1000, { x: image.x - randX, delay: 500 + wait + 100 }, Phaser.Math.Easing.Linear);


            ABCyaPhaser.Tweens.addTween(image, 300, { scale: 0.9, delay: 1500 + wait }, Phaser.Math.Easing.Linear);
            ABCyaPhaser.Tweens.addTween(image, 700, { scale: 1, delay: 1500 + wait + 300 }, Phaser.Math.Easing.Bounce.Out);
        }

        this.loadingMessage.destroy();
        this.loadingMessage = null;

        this.time.delayedCall(2000, this.enableButtons, null, this);
    }

    makeWordBitmap4(evt)
    {
        let wait = 0;

        for (let i = 0; i < this.wordBitmaps.length; i++)
        {
            let image = this.wordBitmaps[i];

            wait += 20;

            if (wait > 500)
            {
                wait = 500;
            }

            ABCyaPhaser.Tweens.addTween(image, 1000, { alpha: 0, delay: wait }, Phaser.Math.Easing.Linear, () => this.pzContainer.removeIt(image));
        }

        let wordImages = evt._images;
        let wordOffsets = evt._offsets;
        //make sure this is empty from last iteration!
        this.wordBitmaps = [];

        wait = 0;

        for (let i = 0; i < wordImages.length; i++)
        {
            let image = wordImages[i];
            this.pzContainer.addIt(image);
            this.wordBitmaps.push(image);

            image.x = wordOffsets[i][0] + 0;
            image.y = wordOffsets[i][1] + 50 + 0;
            image.alpha = 0;

            image.doubleClickAction = this.deleteWord.bind(this);

            wait += 20;

            if (wait > 1000)
            {
                wait = 1000;
            }

            ABCyaPhaser.Tweens.addTween(image, 1000, { alpha: 1, delay: wait + 500 }, Phaser.Math.Easing.Linear);
        }

        this.loadingMessage.destroy();
        this.loadingMessage = null;

        this.time.delayedCall(2000, this.enableButtons, null, this);
    }

    makeWordBitmap5(evt)
    {
        for (let i = 0; i < this.wordBitmaps.length; i++)
        {
            let image = this.wordBitmaps[i];

            ABCyaPhaser.Tweens.addTween(image, 500, { scale: 0 }, Phaser.Math.Easing.Linear, () => this.pzContainer.removeIt(image));
        }

        let wordImages = evt._images;
        let wordOffsets = evt._offsets;
        //make sure this is empty from last iteration!
        this.wordBitmaps = [];

        for (let i = 0; i < wordImages.length; i++)
        {
            let image = wordImages[i];
            this.pzContainer.addIt(image);
            this.wordBitmaps.push(image);

            image.x = wordOffsets[i][0] + 0;
            image.y = wordOffsets[i][1] + 50 + 0;
            image.scaleX = image.scaleY = 0;

            image.doubleClickAction = this.deleteWord.bind(this);

            this.tweens.timeline(
                {
                    tweens:
                        [
                            ABCyaPhaser.Tweens.createTween(image, 1000, { scale: 1, offset: 500 }, Phaser.Math.Easing.Linear),
                            ABCyaPhaser.Tweens.createTween(image, 300, { scale: 1.1, offset: 1500 }, Phaser.Math.Easing.Linear),
                            ABCyaPhaser.Tweens.createTween(image, 700, { scale: 1, offset: 1800 }, Phaser.Math.Easing.Bounce.Out),
                        ]
                }
            );
        }

        this.loadingMessage.destroy();
        this.loadingMessage = null;

        this.time.delayedCall(2000, this.enableButtons, null, this);
    }

    makeWordBitmap6(evt)
    {
        for (let i = 0; i < this.wordBitmaps.length; i++)
        {
            let image = this.wordBitmaps[i];

            ABCyaPhaser.Tweens.addTween(image, 500, { scaleX: -1, scaleY: 1 }, Phaser.Math.Easing.Linear);
            ABCyaPhaser.Tweens.addTween(image, 500, { scaleX: 0, scaleY: 1, delay: 500 }, Phaser.Math.Easing.Linear, () => this.pzContainer.removeIt(image));
        }

        let wordImages = evt._images;
        let wordOffsets = evt._offsets;
        //make sure this is empty from last iteration!
        this.wordBitmaps = [];

        for (let i = 0; i < wordImages.length; i++)
        {
            let image = wordImages[i];
            this.pzContainer.addIt(image);
            this.wordBitmaps.push(image);

            image.x = wordOffsets[i][0] + 0;
            image.y = wordOffsets[i][1] + 50 + 0;
            image.scaleX = 0;

            image.doubleClickAction = this.deleteWord.bind(this);

            this.tweens.timeline(
                {
                    tweens:
                        [
                            ABCyaPhaser.Tweens.createTween(image, 500, { scaleX: -1, scaleY: 1, offset: 500 }, Phaser.Math.Easing.Linear),
                            ABCyaPhaser.Tweens.createTween(image, 500, { scaleX: 0, scaleY: 1, offset: 1000 }, Phaser.Math.Easing.Linear),
                            ABCyaPhaser.Tweens.createTween(image, 500, { scaleX: 1, scaleY: 1, offset: 1500 }, Phaser.Math.Easing.Linear),
                            ABCyaPhaser.Tweens.createTween(image, 300, { scale: 1.1, offset: 2000 }, Phaser.Math.Easing.Linear),
                            ABCyaPhaser.Tweens.createTween(image, 700, { scale: 1, offset: 2300 }, Phaser.Math.Easing.Bounce.Out),
                        ]
                }
            );
        }

        this.loadingMessage.destroy();
        this.loadingMessage = null;

        this.time.delayedCall(2000, this.enableButtons, null, this);
    }

    deleteWord(wordImage)
    {
        //filter this word out when word filter is ON
        gameData.requestedFilteredWords.push(wordImage.word);
        // re-parse words to appyly common word filter (with a newly deleted word)
        this.parseWords();

        let randomXPos = Phaser.Math.Between(0, 1200) - 100;
        let randomYChoice = Phaser.Math.Between(0, 1);

        let randomYPos = -100;
        if (randomYChoice == 1)
        {
            randomYPos = 868;
        }

        ABCyaPhaser.Tweens.addTween(wordImage, 700, { x: randomXPos, y: randomYPos, angle: 2 * 360 }, Phaser.Math.Easing.Linear, () =>
        {
            wordImage.destroy();
            Phaser.Utils.Array.Remove(this.wordBitmaps, wordImage);
            this.makeCloud();
        });
    }

    wordButtonClicked()
    {
        this.menuOptionLayer = new OptionLayer(this, "w", ABCyaPhaser.Images.DEFAULT_ATLAS,
        [
          "10",
          "50",
          "100",
          "200",
          "300",
          "400",
          "500",
          "1000"
        ], 8, 4);
        this.add.existing(this.menuOptionLayer);
        this.menuOptionLayer.on("selected", this.setWordLimit, this);

        //handle dismiss menu events
        this.menuOptionLayer.on("DISMISS", this.handleDismissMenu, this);
    }

    setWordLimit(index)
    {
        switch (index)
        {
            case 1:
                this.wordLimit = 10;
                break;

            case 2:
                this.wordLimit = 50;
                break;

            case 3:
                this.wordLimit = 100;
                break;

            case 4:
                this.wordLimit = 200;
                break;

            case 5:
                this.wordLimit = 300;
                break;

            case 6:
                this.wordLimit = 400;
                break;

            case 7:
                this.wordLimit = 500;
                break;

            case 8:
                this.wordLimit = 1000;
                break;
        }

        this.processWordLimitFrequencies();

        this.wordButton.highlightOff();
        this.menuOptionLayer.destroy();

        this.makeCloud();
    }

    layoutButtonClicked()
    {
        this.menuOptionLayer = new OptionLayer(this, "l", ABCyaPhaser.Images.DEFAULT_ATLAS,null, 0, 0, true);
        this.add.existing(this.menuOptionLayer);
        this.menuOptionLayer.on("selected", this.setLayout, this);

        //handle dismiss menu events
        this.menuOptionLayer.on("DISMISS", this.handleDismissMenu, this);
    }

    setLayout(index)
    {
        switch (index)
        {
            case 1:
                this.layout = "horizontal";
                break;

            case 2:
                this.layout = "vertical";
                break;

            case 3:
                this.layout = "mixed";
                break;

            case 4:
                this.layout = "random";
                break;

            case 5:
                this.shape = "circle";
                break;

            case 6:
                this.shape = "pentagon";
                break;

            case 7:
                this.shape = "triangle";
                break;

            case 8:
                this.shape = "diamond";
                break;

            case 9:
                this.shape = "star";
                break;

            case 10:
                this.shape = "cardioid";
                break;
        }

        this.layoutButton.highlightOff();
        this.menuOptionLayer.destroy();

        this.makeCloud();
    }

    colorButtonClicked()
    {
        let columnCount = 4;

        this.menuOptionLayer = new OptionLayer(this, "c", "color-sprites", null, 12, columnCount);
        this.add.existing(this.menuOptionLayer);

        this.menuOptionLayer.on("selected", this.setColor, this);

        //handle dismiss menu events
        this.menuOptionLayer.on("DISMISS", this.handleDismissMenu, this);
    }

    setColor(index)
    {
        let hslColorArray = [];
        for (let i = ((index - 1) * 5); i < ((index - 1) * 5) + 5; i++)
        {
            let co = this.colorArray[i];
            let hslString = this.decimalToHSL(co[0], co[1], co[2]);
            hslColorArray.push(hslString);
        }

        let bA = this.backColorArray[index - 1];

        let colorFunction = function getRandomColorSet()
        {
            let randNo = Phaser.Math.Between(0, 4);
            let colorString = hslColorArray[randNo];

            return colorString;
        };

        this.fontColor = colorFunction;
        this.backColor = makeHexColor(bA[0], bA[1], bA[2]);

        this.colorButton.highlightOff();
        this.menuOptionLayer.destroy();

        this.makeCloud();
    }

    fontButtonClicked()
    {
        let columnCount = 4;

        this.menuOptionLayer = new OptionLayer(this, "f", ABCyaPhaser.Images.DEFAULT_ATLAS, ABCyaPhaser.Localization.instance.getLocalizedString("game_screen.sample_label"), 12, columnCount);
        this.add.existing(this.menuOptionLayer);
        this.menuOptionLayer.on("selected", this.setFont, this);

        //handle dismiss menu events
        this.menuOptionLayer.on("DISMISS", this.handleDismissMenu, this);
    }

    setFont(index)
    {
        this.fontFamily = this.fontArray[index - 1];

        this.fontButton.highlightOff();

        this.menuOptionLayer.destroy();

        this.makeCloud();
    }
}

function makeHexColor(dec1, dec2, dec3)
{
    let a = dec1.toString(16);
    if (a.length == 1)
    {
        a = "0" + a;
    }

    let b = dec2.toString(16);
    if (b.length == 1)
    {
        b = "0" + b;
    }

    let c = dec3.toString(16);
    if (c.length == 1)
    {
        c = "0" + c;
    }

    let str = "#" + a + b + c;

    return str;
}

function getSortedKeys(obj)
{
    let keys = []; for (let key in obj) keys.push(key);
    return keys.sort(function (a, b) { return obj[a] - obj[b] });
}

function rgbToHsl(r, g, b)
{
    r /= 255, g /= 255, b /= 255;
    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max == min)
    {
        h = s = 0; // achromatic
    }
    else
    {
        let d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max)
        {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }

    return [h, s, l];
}